"use client";

import { useMemo } from "react";
import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import type { CreateNewsDto } from "@/entities/types/entities";
import { Routes } from "@/shared/constants";
import { API_BASE } from "@/shared/constants/private";
import { Button } from "@/shared/ui/Button";
import { CustomImage } from "@/shared/ui/CustomImage";
import { hardNavigate } from "@/shared/utils/hardNavigate";
import { getPostSlug } from "@/shared/utils/postEditor";
import { resolvePostCopy } from "@/shared/utils/postLocale";

import styles from "./HelperBlock.module.scss";

const cx = cnBind.bind(styles);

type Props = {
    articles?: CreateNewsDto[];
    news?: CreateNewsDto[];
    title?: string;
    ctaLabel?: string;
};

export const HelperBlock = ({ news = [], articles = [], title, ctaLabel }: Props) => {
    const { t, i18n } = useTranslation("common");
    const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";

    const formatDate = (date?: string | number | Date) => {
        if (!date) return "";
        try {
            return new Date(date).toLocaleDateString(dateLocale, {
                day: "numeric",
                month: "long",
                year: "numeric",
            });
        } catch {
            return "";
        }
    };
    const articleCards = useMemo(
        () =>
            (articles ?? []).map((a) => ({
                type: "article" as const,
                id: a.id,
                title: resolvePostCopy(a, i18n.language).title,
                href: `${Routes.ARTICLES}/${encodeURIComponent(getPostSlug(a))}`,
                image: a.pictureId,
                excerpt: resolvePostCopy(a, i18n.language).subtitle,
                dateLabel: formatDate(a?.createdAt),
                dateMs: a?.createdAt ? new Date(a.createdAt).getTime() : 0,
            })),
        [articles, dateLocale, i18n.language],
    );

    const newsCards = useMemo(
        () =>
            (news ?? []).map((n) => ({
                type: "news" as const,
                id: n.id,
                title: resolvePostCopy(n, i18n.language).title,
                href: `${Routes.ARTICLES}/${encodeURIComponent(getPostSlug(n))}`,
                image: n.pictureId,
                excerpt: resolvePostCopy(n, i18n.language).subtitle,
                dateLabel: formatDate(n?.createdAt),
                dateMs: n?.createdAt ? new Date(n.createdAt).getTime() : 0,
            })),
        [news, dateLocale, i18n.language],
    );

    const top3 = useMemo(() => {
        const byDate = (a: { dateMs: number }, b: { dateMs: number }) => b.dateMs - a.dateMs;
        const newsSorted = [...newsCards].sort(byDate);
        if (newsSorted.length >= 3) return newsSorted.slice(0, 3);
        const used = new Set(newsSorted.map((item) => item.id));
        const extra = [...articleCards].sort(byDate).filter((item) => !used.has(item.id));
        return [...newsSorted, ...extra].slice(0, 3);
    }, [articleCards, newsCards]);

    if (top3.length === 0) {
        return null;
    }

    return (
        <section className={cx("helper-block")} id="news" aria-labelledby="news-block-title">
            <div className={cx("wrapper", "container")}>
                <h2 id="news-block-title" className={cx("title")}>
                    {title || t("home.helper.titleNews")}
                </h2>

                <div className={cx("cards")}>
                    {top3.map((item, index) => (
                        <a
                            key={`${item.type}-${item.id}`}
                            href={item.href}
                            className={cx("card", { "card--wide": index % 3 === 2 })}
                            aria-label={item.title}
                        >
                            <div className={cx("card__image")}>
                                <CustomImage
                                    src={`${API_BASE}/picture/${item.image}`}
                                    alt={item.title}
                                    width={1280}
                                    height={720}
                                />
                            </div>

                            <div className={cx("card__body")}>
                                <h3 className={cx("card__title")}>{item.title}</h3>
                                {item.dateLabel && <div className={cx("card__date")}>{item.dateLabel}</div>}
                                {item.excerpt && <p className={cx("card__excerpt")}>{item.excerpt}</p>}
                            </div>
                        </a>
                    ))}
                </div>

                <div className={cx("footer")}>
                    <Button
                        onClick={() => hardNavigate(Routes.ARTICLES)}
                        label={ctaLabel || t("home.helper.ctaAllNews")}
                        variant="white"
                        iconTrue={false}
                    />
                </div>
            </div>
        </section>
    );
};
